import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory } from 'react-router-dom';
import { createSession } from 'flow/actions/chat-actions';
import { handleCtiClick } from 'flow/actions/cti-actions';
import {
    createPresenceNotification,
    deletePresenceNotification,
    fetchCallRecordWithEntityKey,
    removeVoiceMessage,
} from 'flow/actions/extension-actions';
import Divider from 'flow/components/ui-components/Divider';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { selectChatSessionByChatUser } from 'flow/selectors/chat-selectors';
import { getSelectedCtiContactByNumber } from 'flow/selectors/cti-integration-selectors';
import {
    getExtensionKeyFromContactKey,
    getChatUserKeyForExtensionWithContactKey,
    getExtension,
    canSetPresenceNotification,
    selectOwnExtensionKey,
    selectOwnUserHasCapabilityVideo1on1,
} from 'flow/selectors/ExtensionSelectors';

import { getHasPresenceNotification } from 'flow/selectors/selectors';

import { isExtensionKey, voiceMessageUrlPath } from 'flow/utils';
import { ContactType } from 'flow/utils/extension-utils';
import CallButtonMenuItem from './CallButtonMenuItem';
import EmailFieldMenuItem from './EmailFieldMenuItem';
import VideoCallButtonMenuItem from './VideoCallButtonMenuItem';
import { routes } from 'flow/types/routes';
import { ExtensionVoiceMessage } from 'flow/types/VoiceMessage';
import { TypedMap } from 'flow/types/TypedMap';

interface Props {
    voiceMessage: TypedMap<ExtensionVoiceMessage>;
    onClickCreateContact: () => void;
}

const VoicemailContextMenu = ({
    voiceMessage,
    onClickCreateContact,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const ownUserHasVideo1on1Capability = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { hasFeature } = useFeatureFlags();

    const voiceMessageCallKey = voiceMessage.get('call');

    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, voiceMessageCallKey),
    );

    useEffect(() => {
        if (!callRecord) {
            dispatch(fetchCallRecordWithEntityKey(voiceMessageCallKey));
        }
    }, []);

    const deleteVoiceMessage = (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(
            removeVoiceMessage(
                ownExtensionKey,
                voiceMessage.get('uniqueId'),
                callRecord?.get('key'),
            ),
        );
    };
    const callRecordContact = callRecord?.get('contact');

    const targetExtensionKey = useAppSelector((state) =>
        //@ts-ignore
        getExtensionKeyFromContactKey(state, callRecordContact?.get('key')),
    );

    const targetExtension = useAppSelector((state) =>
        isExtensionKey(targetExtensionKey)
            ? getExtension(state, targetExtensionKey)
            : undefined,
    );

    const contactKey = callRecordContact?.get('key');

    const chatUserKey = useAppSelector((state) =>
        //@ts-ignore
        getChatUserKeyForExtensionWithContactKey(state, contactKey),
    );

    const chatSessionKey = useAppSelector((state) =>
        selectChatSessionByChatUser(state, chatUserKey, ownExtensionKey),
    );

    const navigateToChat = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        return chatSessionKey
            ? history.push(
                  generatePath(routes['messages.id'], { id: chatSessionKey }),
              )
            : dispatch(createSession(chatUserKey));
    };

    const phoneNumber = callRecord?.getIn(['number', 'number']);

    const integrationContact = useAppSelector((state) =>
        getSelectedCtiContactByNumber(state, phoneNumber),
    );

    const navigateToContact = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (integrationContact) {
            dispatch(
                handleCtiClick(integrationContact.get('contactRoute'), {
                    number: integrationContact.get('number'),
                }),
            );
        } else if (!targetExtension) {
            history.push(
                generatePath(routes['calls.callRecordKey.contact'], {
                    callRecordKey: callRecord?.get('key'),
                }),
            );
        } else {
            history.push(
                generatePath(routes['extensions.entityKey.contact'], {
                    entityKey: targetExtension.get('key'),
                }),
            );
        }
    };

    const emailField = callRecordContact?.get('email');
    const isColleague = callRecordContact
        ? callRecordContact.get('type') === ContactType.bound.key
        : false;
    const showNotifyWhenAvailable = useAppSelector((state) =>
        canSetPresenceNotification(state, targetExtensionKey),
    );

    const presenceNotify = useAppSelector((state) =>
        getHasPresenceNotification(state, ownExtensionKey, targetExtensionKey),
    );

    const togglePresenceNotification = (
        event: React.MouseEvent<HTMLDivElement>,
    ) => {
        if (presenceNotify) {
            dispatch(
                deletePresenceNotification(ownExtensionKey, targetExtension),
            );
        } else {
            dispatch(
                createPresenceNotification(ownExtensionKey, targetExtension),
            );
        }
    };

    const fixedNumber = callRecordContact?.getIn(['fixed', 'number']);
    const mobileNumber = callRecordContact?.getIn(['mobile', 'number']);
    const technicalNumber = callRecordContact?.getIn([
        'technicalNumber',
        'number',
    ]);

    const canMakeVideoCall =
        targetExtension &&
        ownUserHasVideo1on1Capability &&
        hasFeature(FeatureFlags.VIDEO_CANVAS);

    return (
        <>
            <CallButtonMenuItem
                fixedNumber={fixedNumber}
                technicalNumber={technicalNumber}
                mobileNumber={mobileNumber}
                extensionToCall={targetExtension}
                unknownContactNumber={callRecordContact?.getIn([
                    'number',
                    'number',
                ])}
            />

            {canMakeVideoCall && (
                <VideoCallButtonMenuItem
                    extensionKey={targetExtension.get('key')}
                />
            )}

            {chatUserKey ? (
                <MenuItem onClick={navigateToChat}>
                    {i18next.t('Tooltip.Title.SendMessage')}
                </MenuItem>
            ) : null}
            {!(callRecordContact || integrationContact) ? (
                <MenuItem onClick={onClickCreateContact}>
                    {i18next.t('Colleagues.Create.External.Title')}
                </MenuItem>
            ) : (
                <MenuItem onClick={navigateToContact}>
                    {i18next.t('Chat.Details.GoToContactCard')}
                </MenuItem>
            )}
            {(emailField || (isColleague && showNotifyWhenAvailable)) && (
                <>
                    <Divider className="my-2" />

                    <EmailFieldMenuItem emailField={emailField} />
                </>
            )}

            {isColleague && showNotifyWhenAvailable && (
                <MenuItem onClick={togglePresenceNotification}>
                    <div>
                        {i18next.t(
                            'General.ExtensionBusyAlet.NotifyWhenAvailable',
                        )}
                    </div>
                    {presenceNotify ? (
                        <div>
                            <Icon iconKey={'done'} />
                        </div>
                    ) : null}
                </MenuItem>
            )}

            <Divider className="my-2" />

            <a
                href={voiceMessageUrlPath(
                    ownExtensionKey,
                    voiceMessage.get('uniqueId'),
                )}
                download={true}
            >
                <MenuItem>
                    <div className="flex items-center gap-x-2">
                        <Icon iconKey="get_app" />
                        {i18next.t('call.voicemail.download')}
                    </div>
                </MenuItem>
            </a>

            <MenuItem onClick={deleteVoiceMessage}>
                <div className="flex items-center gap-x-2">
                    <Icon iconKey="delete" />
                    {i18next.t('call.voicemail.delete')}
                </div>
            </MenuItem>
        </>
    );
};

export default VoicemailContextMenu;
